import { useState, useEffect } from "react";
import Landing from "./pages/Landing";
import MobilityMarket from "./pages/MobilityMarket";
import Story from "./pages/Story";
import AirportAccess from "./pages/AirportAccess";
import LandingUS from "./pages/us/LandingUS";
import StoryUS from "./pages/us/StoryUS";
import DashboardUS from "./pages/us/DashboardUS";
import "./styles/global.css";

export type Page =
  | "landing"
  | "mobility"
  | "story"
  | "dashboard"
  | "usLanding"
  | "usStory"
  | "usDashboard";

const PAGE_ACCENT: Record<Page, string> = {
  landing: "#009A84",
  mobility: "#009A84",
  story: "#009A84",
  dashboard: "#009A84",
  usLanding: "#009A84",
  usStory: "#009A84",
  usDashboard: "#009A84",
};

// Pages de la section 
// de la même section plutôt que vers l'accueil India.
const US_PAGES = new Set<Page>(["usLanding", "usStory", "usDashboard"]);

function hexToRgba(hex: string, alpha: number) {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function GlobalTopBar({ page, setPage }: { page: Page; setPage: (p: Page) => void }) {
  const accent = PAGE_ACCENT[page];
  const homePage: Page = US_PAGES.has(page) ? "usLanding" : "landing";

  return (
    <>
      
      <div
  className="global-top-bar"
  style={{
    "--accent": accent,
    "--accent-30": hexToRgba(accent, 0.4),
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 4000,
    display: "flex",
    alignItems: "center",
    background: "rgba(6,6,8,0.88)",
    borderBottom: `1px solid ${hexToRgba(accent, 0.15)}`,
    backdropFilter: "blur(20px)",
    fontFamily: "'ATR Innova', sans-serif",
    transition: "border-color 0.3s ease",
  } as React.CSSProperties}
>
        <div
       
          onMouseEnter={(e) => {
            const white = e.currentTarget.querySelector(".atr-logo-white") as HTMLElement;
            const red = e.currentTarget.querySelector(".atr-logo-red") as HTMLElement;
            if (white) white.style.opacity = "1";
            if (red) {
              red.style.opacity = "0";
              red.style.filter = "drop-shadow(0 0 0 rgba(235,40,15,0))";
            }
            e.currentTarget.style.transform = "scale(1.04)";
          }}
          onMouseLeave={(e) => {
            const white = e.currentTarget.querySelector(".atr-logo-white") as HTMLElement;
            const red = e.currentTarget.querySelector(".atr-logo-red") as HTMLElement;
            if (white) white.style.opacity = "0";
            if (red) {
              red.style.opacity = "1";
              red.style.filter = "drop-shadow(0 0 10px rgba(235,40,15,0.5))";
            }
            e.currentTarget.style.transform = "scale(1)";
          }}
          style={{
            background: "transparent",
            border: "none",
            padding: 0,
           cursor: "default",
            display: "flex",
            alignItems: "center",
            transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
            flexShrink: 0,
          }}
          title="Back to home"
        >
          <div style={{ position: "relative", height: 20 }}>
            <img
              className="atr-logo-white"
              src="/photos/ATR_RGB_White.png"
              alt="ATR"
              style={{
                height: 20,
                display: "block",
                transition: "opacity 0.35s ease",
                opacity: 0,
              }}
            />
            <img
              className="atr-logo-red"
              src="/photos/ATR_RGB_Radiant_Sun.png"
              alt="ATR"
              style={{
                height: 20,
                display: "block",
                position: "absolute",
                top: 0,
                left: 0,
                transition: "opacity 0.35s ease, filter 0.35s ease",
                opacity: 1,
                filter: "drop-shadow(0 0 10px rgba(235,40,15,0.5))",
              }}
            />
          </div>
        </div>
        
      </div>
    </>
  );
}

const BASE_WIDTH = 1600;
const BASE_HEIGHT = 900;

function FixedViewport({ children, natural }: { children: React.ReactNode; natural: boolean }) {
  // India pages: the 1600x900 reference canvas is stretched to fill the real
  // screen (their layouts use absolute px positions tuned for that canvas).
  //
  // US pages (`natural = true`): NEVER use the scale-transform, at any
  // screen size. That transform scaled X and Y independently to match the
  // screen's real aspect ratio, which distorts everything non-uniformly on
  // any screen that isn't exactly 16:9 — i.e. basically every laptop,
  // iPad, and ultrawide monitor. US pages instead render at their true
  // pixel size and rely entirely on real responsive CSS (flex/grid/clamp +
  // media queries) to adapt across phone / tablet / laptop / desktop.
  const [scale, setScale] = useState({ x: 1, y: 1 });

  useEffect(() => {
    if (natural) return;
    const computeScale = () => {
      setScale({
        x: window.innerWidth / BASE_WIDTH,
        y: window.innerHeight / BASE_HEIGHT,
      });
    };
    computeScale();
    window.addEventListener("resize", computeScale);
    return () => window.removeEventListener("resize", computeScale);
  }, [natural]);

  if (natural) {
    return (
      <div style={{ position: "relative", width: "100%", height: "100vh", overflow: "hidden", background: "#00313C" }}>
        {children}
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden", background: "#00313C" }}>
      <div
        style={{
          width: BASE_WIDTH,
          height: BASE_HEIGHT,
          transform: `scale(${scale.x}, ${scale.y})`,
          transformOrigin: "top left",
          position: "absolute",
          top: 0,
          left: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function App() {
  // Default page set to the new US RAA landing for preview purposes.
  // Switch back to "landing" to land on the original India homepage.
  const [page, setPage] = useState<Page>("usLanding");

  const renderPage = () => {
    if (page === "mobility") return <MobilityMarket navigate={setPage} />;
    if (page === "story") return <Story navigate={setPage} />;
    if (page === "dashboard") return <AirportAccess navigate={setPage} />;
    if (page === "usLanding") return <LandingUS navigate={setPage} />;
    if (page === "usStory") return <StoryUS navigate={setPage} />;
    if (page === "usDashboard") return <DashboardUS navigate={setPage} />;
    return <Landing navigate={setPage} />;
  };

  return (
    <FixedViewport natural={US_PAGES.has(page)}>
      {renderPage()}
      <GlobalTopBar page={page} setPage={setPage} />
    </FixedViewport>
  );
}